import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Search, Cloud, Server, Database, ChevronRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
interface StartScanModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const MOCK_ENVIRONMENTS = [{
  id: 'ENV-001',
  clientName: 'Acme Corp',
  type: 'Hybrid',
  accounts: 3
}, {
  id: 'ENV-002',
  clientName: 'Globex Inc',
  type: 'AWS',
  accounts: 5
}, {
  id: 'ENV-003',
  clientName: 'Soylent Corp',
  type: 'On-Prem',
  accounts: 1
}, {
  id: 'ENV-004',
  clientName: 'Umbrella Corp',
  type: 'Azure',
  accounts: 2
}, {
  id: 'ENV-005',
  clientName: 'Stark Ind',
  type: 'GCP',
  accounts: 4
}];
export function StartScanModal({
  isOpen,
  onClose
}: StartScanModalProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedEnvId, setSelectedEnvId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedEnvId(null);
      setSearchQuery('');
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const selectedEnv = MOCK_ENVIRONMENTS.find(e => e.id === selectedEnvId);
  const filteredEnvs = MOCK_ENVIRONMENTS.filter(e => e.clientName.toLowerCase().includes(searchQuery.toLowerCase()) || e.type.toLowerCase().includes(searchQuery.toLowerCase()));
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'AWS':
      case 'Azure':
      case 'GCP':
        return <Cloud size={16} data-id="element-748" />;
      case 'On-Prem':
        return <Server size={16} data-id="element-749" />;
      case 'Hybrid':
        return <Database size={16} data-id="element-750" />;
      default:
        return <Server size={16} data-id="element-751" />;
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-752">
      <div className="w-full max-w-[500px] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]" data-id="element-753">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border shrink-0" data-id="element-754">
          <div className="flex items-center gap-3" data-id="element-755">
            {step === 2 && <button onClick={() => setStep(1)} className="text-astro-text-secondary hover:text-white transition-colors" data-id="element-756">
                <ArrowLeft size={18} data-id="element-757" />
              </button>}
            <h2 className="text-lg font-bold text-white" data-id="element-758">
              {step === 1 ? 'Select Scan Target' : 'Confirm Scan'}
            </h2>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-759">
            <X size={20} data-id="element-760" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" data-id="element-761">
          {step === 1 ? <>
              <div className="relative mb-4" data-id="element-762">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" size={16} data-id="element-763" />
                <input type="text" placeholder="Search environments..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-[#0a0e14] border border-astro-border rounded-md pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-764" />
              </div>

              <div className="space-y-2 mb-6 max-h-[300px] overflow-y-auto pr-1" data-id="element-765">
                {filteredEnvs.map(env => <button key={env.id} onClick={() => setSelectedEnvId(env.id)} className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${selectedEnvId === env.id ? 'bg-astro-green/10 border-astro-green/50' : 'bg-[#0a0e14] border-astro-border hover:border-white/20'}`} data-id="element-766">
                    <div className="flex items-center gap-3" data-id="element-767">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${selectedEnvId === env.id ? 'bg-astro-green/20 text-astro-green' : 'bg-white/5 text-astro-text-secondary'}`} data-id="element-768">
                        {getTypeIcon(env.type)}
                      </div>
                      <div data-id="element-769">
                        <div className="text-sm font-bold text-white" data-id="element-770">
                          {env.clientName}
                        </div>
                        <div className="text-xs text-astro-text-secondary flex items-center gap-2 mt-0.5" data-id="element-771">
                          <span data-id="element-772">{env.type}</span>
                          <span className="w-1 h-1 rounded-full bg-astro-border" data-id="element-773"></span>
                          <span data-id="element-774">
                            {env.accounts} account
                            {env.accounts !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    {selectedEnvId === env.id && <CheckCircle2 size={18} className="text-astro-green" data-id="element-775" />}
                  </button>)}
                {filteredEnvs.length === 0 && <div className="text-center py-8 text-astro-text-secondary text-sm" data-id="element-776">
                    No environments found.
                  </div>}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-astro-border" data-id="element-777">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-778">
                  Cancel
                </button>
                <button onClick={() => setStep(2)} disabled={!selectedEnvId} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-black bg-astro-green hover:bg-astro-green-dark rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.2)] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-779">
                  Next Step <ChevronRight size={16} data-id="element-780" />
                </button>
              </div>
            </> : <>
              <div className="bg-[#0a0e14] border border-astro-border rounded-md p-4 mb-6" data-id="element-781">
                <div className="flex justify-between items-center mb-4 pb-3 border-b border-astro-border/50" data-id="element-782">
                  <span className="text-sm font-medium text-white" data-id="element-783">
                    Scan Target Details
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-astro-green/10 text-astro-green border border-astro-green/20" data-id="element-784">
                    Ready
                  </span>
                </div>
                <div className="space-y-3 text-sm" data-id="element-785">
                  <div className="flex justify-between items-center" data-id="element-786">
                    <span className="text-astro-text-secondary" data-id="element-787">Client:</span>
                    <span className="font-bold text-white" data-id="element-788">
                      {selectedEnv?.clientName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-789">
                    <span className="text-astro-text-secondary" data-id="element-790">Platform:</span>
                    <span className="flex items-center gap-1.5 text-white" data-id="element-791">
                      {selectedEnv && getTypeIcon(selectedEnv.type)}
                      {selectedEnv?.type}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-792">
                    <span className="text-astro-text-secondary" data-id="element-793">Scope:</span>
                    <span className="text-white" data-id="element-794">
                      {selectedEnv?.accounts} connected account
                      {selectedEnv?.accounts !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-795">
                    <span className="text-astro-text-secondary" data-id="element-796">
                      Environment ID:
                    </span>
                    <span className="font-mono text-astro-text-secondary text-xs" data-id="element-797">
                      {selectedEnv?.id}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mb-6 p-4 bg-astro-card border border-astro-border rounded-lg" data-id="element-798">
                <AlertTriangle className="text-astro-text-secondary shrink-0 mt-0.5" size={16} data-id="element-799" />
                <div data-id="element-800">
                  <h4 className="text-sm font-bold text-white mb-1" data-id="element-801">
                    This scan may take several minutes
                  </h4>
                  <p className="text-xs text-astro-text-secondary leading-relaxed" data-id="element-802">
                    Scan duration depends on the number of resources and
                    identities in the target environment. You'll receive an
                    email notification when the scan completes.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-astro-border" data-id="element-803">
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-804">
                  Cancel
                </button>
                <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-black bg-astro-green hover:bg-astro-green-dark rounded transition-colors shadow-[0_0_15px_rgba(34,197,94,0.2)]" data-id="element-805">
                  Start Scan
                </button>
              </div>
            </>}
        </div>
      </div>
    </div>;
}